export function averageHash(imageData, cellsX = 16, cellsY = 9) {
  const { data, width, height } = imageData;
  const values = [];
  for (let cellY = 0; cellY < cellsY; cellY += 1) {
    for (let cellX = 0; cellX < cellsX; cellX += 1) {
      const x = Math.min(width - 1, Math.floor(((cellX + 0.5) / cellsX) * width));
      const y = Math.min(height - 1, Math.floor(((cellY + 0.5) / cellsY) * height));
      const offset = (y * width + x) * 4;
      values.push(Math.round((data[offset] * 0.299 + data[offset + 1] * 0.587 + data[offset + 2] * 0.114) / 16));
    }
  }
  return values;
}

export function hashDifference(first, second) {
  if (!first?.length || first.length !== second?.length) return 1;
  let difference = 0;
  for (let index = 0; index < first.length; index += 1) {
    difference += Math.abs(first[index] - second[index]) / 16;
  }
  return difference / first.length;
}

export const BELOTE_CAPTURE_REGIONS = Object.freeze([
  { id: "score", x: 0, y: 0, width: 0.25, height: 0.22 },
  { id: "name-self", x: 0.25, y: 0.83, width: 0.5, height: 0.12 },
  { id: "player-top", x: 0.28, y: 0, width: 0.44, height: 0.24 },
  { id: "player-left", x: 0, y: 0.16, width: 0.34, height: 0.5 },
  { id: "player-right", x: 0.66, y: 0.16, width: 0.34, height: 0.5 },
  { id: "bid-contract", x: 0.3, y: 0.25, width: 0.4, height: 0.28 },
  { id: "table", x: 0.22, y: 0.18, width: 0.56, height: 0.5 },
  { id: "hand", x: 0.06, y: 0.6, width: 0.88, height: 0.4 },
]);

export function regionAverageHash(imageData, region, cellsX = 8, cellsY = 6) {
  const { data, width, height } = imageData;
  if (!data?.length || !width || !height || !region) return [];
  const left = Math.max(0, Math.min(width - 1, Math.floor(region.x * width)));
  const top = Math.max(0, Math.min(height - 1, Math.floor(region.y * height)));
  const regionWidth = Math.max(1, Math.floor(region.width * width));
  const regionHeight = Math.max(1, Math.floor(region.height * height));
  const values = [];

  for (let cellY = 0; cellY < cellsY; cellY += 1) {
    for (let cellX = 0; cellX < cellsX; cellX += 1) {
      const x = Math.min(width - 1, left + Math.floor(((cellX + 0.5) / cellsX) * regionWidth));
      const y = Math.min(height - 1, top + Math.floor(((cellY + 0.5) / cellsY) * regionHeight));
      const offset = (y * width + x) * 4;
      values.push(Math.round((data[offset] * 0.299 + data[offset + 1] * 0.587 + data[offset + 2] * 0.114) / 16));
    }
  }
  return values;
}

export function captureRegionHashes(imageData, regions = BELOTE_CAPTURE_REGIONS) {
  return Object.fromEntries(regions.map((region) => [region.id, regionAverageHash(imageData, region)]));
}

export function changedCaptureRegions(previous, current, threshold = 0.035) {
  if (!current) return [];
  return Object.keys(current).filter((id) => !previous?.[id] || hashDifference(previous[id], current[id]) >= threshold);
}

export function shouldPersistTimelineFrame({
  force = false,
  enabled = true,
  phase = "unknown",
  changedRegions = [],
  now = 0,
  lastSavedAt = 0,
  minIntervalMs = 90,
  heartbeatMs = 1800,
} = {}) {
  if (force) return true;
  if (!enabled || phase === "unknown") return false;
  const elapsed = Math.max(0, now - lastSavedAt);
  if (elapsed < minIntervalMs) return false;
  return changedRegions.length > 0 || elapsed >= heartbeatMs;
}

export function stripTemporaryCapture(record, purgedAt = new Date().toISOString()) {
  if (!record) return record;
  return {
    ...record,
    image: null,
    imageTemporary: false,
    imagePurgedAt: purgedAt,
  };
}

export function classifyFrame(imageData) {
  const { data } = imageData;
  let green = 0;
  let orange = 0;
  let white = 0;
  let red = 0;
  const pixels = data.length / 4;

  for (let offset = 0; offset < data.length; offset += 16) {
    const r = data[offset];
    const g = data[offset + 1];
    const b = data[offset + 2];
    if (g > 55 && g > r * 1.18 && g > b * 1.1) green += 4;
    if (r > 185 && g > 70 && g < 165 && b < 75) orange += 4;
    if (r > 218 && g > 218 && b > 210) white += 4;
    if (r > 160 && g < 90 && b < 90) red += 4;
  }

  const ratios = {
    green: green / pixels,
    orange: orange / pixels,
    white: white / pixels,
    red: red / pixels,
  };

  let phase = "unknown";
  if (ratios.white > 0.55 && ratios.green < 0.16) phase = "lobby";
  else if (ratios.green > 0.2 && ratios.orange > 0.006 && ratios.white > 0.025) phase = "bidding";
  else if (ratios.green > 0.2 && ratios.white > 0.035) phase = "play";
  else if (ratios.green > 0.18 && ratios.red > 0.002) phase = "result";
  else if (ratios.green > 0.12) phase = "table";
  return { phase, ratios };
}

function greenPixel(data, offset) {
  const r = data[offset];
  const g = data[offset + 1];
  const b = data[offset + 2];
  return g > 42 && g > r * 1.12 && g > b * 1.04;
}

function whitePixel(data, offset) {
  const r = data[offset];
  const g = data[offset + 1];
  const b = data[offset + 2];
  return r > 176 && g > 176 && b > 166 && Math.max(r, g, b) - Math.min(r, g, b) < 72;
}

function darkPixel(data, offset) {
  return data[offset] < 112 && data[offset + 1] < 112 && data[offset + 2] < 112;
}

function connectedComponents(mask, width, height) {
  const visited = new Uint8Array(mask.length);
  const components = [];
  const neighbours = [-1, 1, -width, width, -width - 1, -width + 1, width - 1, width + 1];

  for (let start = 0; start < mask.length; start += 1) {
    if (!mask[start] || visited[start]) continue;
    const stack = [start];
    visited[start] = 1;
    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;
    let pixels = 0;

    while (stack.length) {
      const index = stack.pop();
      const x = index % width;
      const y = Math.floor(index / width);
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
      pixels += 1;

      for (const delta of neighbours) {
        const next = index + delta;
        if (next < 0 || next >= mask.length || visited[next] || !mask[next]) continue;
        const nextX = next % width;
        const nextY = Math.floor(next / width);
        if (Math.abs(nextX - x) > 1 || Math.abs(nextY - y) > 1) continue;
        visited[next] = 1;
        stack.push(next);
      }
    }
    components.push({ minX, minY, maxX, maxY, pixels });
  }
  return components;
}

function greenRingRatio(imageData, bounds) {
  const { data, width, height } = imageData;
  const side = Math.max(bounds.maxX - bounds.minX + 1, bounds.maxY - bounds.minY + 1);
  const padding = Math.max(2, Math.round(side * 0.28));
  const left = Math.max(0, bounds.minX - padding);
  const right = Math.min(width - 1, bounds.maxX + padding);
  const top = Math.max(0, bounds.minY - padding);
  const bottom = Math.min(height - 1, bounds.maxY + padding);
  let green = 0;
  let sampled = 0;

  for (let y = top; y <= bottom; y += 2) {
    for (let x = left; x <= right; x += 2) {
      if (x >= bounds.minX && x <= bounds.maxX && y >= bounds.minY && y <= bounds.maxY) continue;
      sampled += 1;
      if (greenPixel(data, (y * width + x) * 4)) green += 1;
    }
  }
  return sampled ? green / sampled : 0;
}

function countPips(imageData, bounds) {
  const { data, width } = imageData;
  const boxWidth = bounds.maxX - bounds.minX + 1;
  const boxHeight = bounds.maxY - bounds.minY + 1;
  const marginX = Math.max(1, Math.round(boxWidth * 0.1));
  const marginY = Math.max(1, Math.round(boxHeight * 0.1));
  const innerWidth = Math.max(1, boxWidth - marginX * 2);
  const innerHeight = Math.max(1, boxHeight - marginY * 2);
  const mask = new Uint8Array(innerWidth * innerHeight);

  for (let y = 0; y < innerHeight; y += 1) {
    for (let x = 0; x < innerWidth; x += 1) {
      const sourceX = bounds.minX + marginX + x;
      const sourceY = bounds.minY + marginY + y;
      if (darkPixel(data, (sourceY * width + sourceX) * 4)) mask[y * innerWidth + x] = 1;
    }
  }

  const tileArea = boxWidth * boxHeight;
  return connectedComponents(mask, innerWidth, innerHeight).filter((component) => {
    const componentWidth = component.maxX - component.minX + 1;
    const componentHeight = component.maxY - component.minY + 1;
    const aspect = componentWidth / componentHeight;
    return component.pixels >= Math.max(1, tileArea * 0.002)
      && component.pixels <= tileArea * 0.085
      && aspect >= 0.45
      && aspect <= 2.2;
  }).length;
}

export function detectDice(imageData) {
  const { data, width, height } = imageData;
  if (!data?.length || !width || !height) return [];
  const whiteMask = new Uint8Array(width * height);
  for (let index = 0; index < whiteMask.length; index += 1) {
    if (whitePixel(data, index * 4)) whiteMask[index] = 1;
  }

  const minSide = Math.max(7, Math.round(Math.min(width, height) * 0.012));
  const maxSide = Math.max(28, Math.round(Math.min(width, height) * 0.13));
  return connectedComponents(whiteMask, width, height)
    .map((component) => {
      const boxWidth = component.maxX - component.minX + 1;
      const boxHeight = component.maxY - component.minY + 1;
      const aspect = boxWidth / boxHeight;
      const fill = component.pixels / (boxWidth * boxHeight);
      if (boxWidth < minSide || boxHeight < minSide || boxWidth > maxSide || boxHeight > maxSide) return null;
      if (aspect < 0.72 || aspect > 1.38 || fill < 0.36) return null;
      const greenRatio = greenRingRatio(imageData, component);
      if (greenRatio < 0.32) return null;
      const value = countPips(imageData, component);
      if (value < 1 || value > 6) return null;
      const centerX = (component.minX + component.maxX) / 2;
      const centerY = (component.minY + component.maxY) / 2;
      return {
        value,
        x: centerX,
        y: centerY,
        width: boxWidth,
        height: boxHeight,
        normalizedX: centerX / width,
        normalizedY: centerY / height,
        confidence: Math.min(1, greenRatio * 0.55 + fill * 0.45),
      };
    })
    .filter(Boolean)
    .sort((first, second) => first.x - second.x || first.y - second.y);
}

export function stableDiceFrame(frames, { requiredFrames = 3, maxShift = 4 } = {}) {
  const usable = frames.slice(-requiredFrames);
  if (usable.length !== requiredFrames || usable.some((frame) => frame.length !== 5)) return null;
  const reference = usable.at(-1);
  const referenceValues = reference.map((die) => die.value).sort().join(",");
  for (let frameIndex = 0; frameIndex < usable.length - 1; frameIndex += 1) {
    const previousFrame = usable[frameIndex];
    if (previousFrame.map((die) => die.value).sort().join(",") !== referenceValues) return null;
    const unmatched = [...previousFrame];
    for (const current of reference) {
      let closestIndex = -1;
      let closestDistance = Infinity;
      unmatched.forEach((previous, index) => {
        if (previous.value !== current.value) return;
        const distance = Math.hypot(previous.x - current.x, previous.y - current.y);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      if (closestIndex < 0 || closestDistance > maxShift) return null;
      unmatched.splice(closestIndex, 1);
    }
  }
  return reference;
}

export function diceValueCounts(dice) {
  return dice.reduce((counts, die) => {
    counts[die.value] = (counts[die.value] || 0) + 1;
    return counts;
  }, { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 });
}

export function serializeAnalysis(analysis) {
  if (!analysis?.bySuit) return null;
  return {
    iterations: analysis.iterations,
    position: analysis.position,
    returnedSuit: analysis.returned.suit,
    bestAlternativeSuit: analysis.bestAlternative.suit,
    bySuit: Object.fromEntries(
      Object.entries(analysis.bySuit).map(([suit, result]) => [
        suit,
        {
          probability: result.probability,
          expectedPoints: result.expectedPoints,
          expectedTricks: result.expectedTricks,
          ci95: result.ci95,
        },
      ]),
    ),
  };
}

export function createRecordId(prefix = "record") {
  const random = Math.random().toString(36).slice(2, 9);
  return `${prefix}-${Date.now().toString(36)}-${random}`;
}

export function safeInputSnapshot(input) {
  return {
    hand: (input?.hand || []).map((card) => (typeof card === "string" ? card : card.id)),
    center: typeof input?.center === "string" ? input.center : input?.center?.id || null,
    position: input?.position || null,
  };
}
